import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';

import { isFunction } from 'lib/tsUtils';

import {
  type SnowplowPageViewContextValueType,
  useSnowplowPageViewContext,
} from './SnowplowProvider';

import type { SnowplowPageViewContextType } from '../types';

const DEFAULT_CONTEXT_READINESS_TIMEOUT_IN_MILLISECONDS = 2000;

type HasChildSetterContextType = React.Dispatch<
  React.SetStateAction<boolean | undefined>
>;
const HasChildSetterContext = createContext<
  HasChildSetterContextType | undefined
>(undefined);

type SnowplowPageContextType = {
  setPageViewContext: SnowplowPageViewContextValueType['setContext'];
};

const SnowplowPageContext = createContext<SnowplowPageContextType | undefined>(
  undefined,
);

type SnowplowPageProps = {
  isContextReady?:
    | boolean
    | ((context: SnowplowPageViewContextType) => boolean);
  contextReadinessTimeoutInMilliseconds?: number;
} & PropsWithChildren;

export const SnowplowPage = ({
  children,
  isContextReady = true,
  contextReadinessTimeoutInMilliseconds = DEFAULT_CONTEXT_READINESS_TIMEOUT_IN_MILLISECONDS,
}: SnowplowPageProps) => {
  const {
    setContext: setContextInSnowplowProvider,
    pause,
    resume,
  } = useSnowplowPageViewContext();
  const [context, setPageViewContext] =
    useState<SnowplowPageViewContextType>(null);
  const value = useRef({ setPageViewContext });

  const [hasChild, setHasChild] = useState<boolean>();
  const setHasChildInParent = useContext(HasChildSetterContext);
  useEffect(() => {
    if (setHasChildInParent) {
      setHasChildInParent(true);
      return () => setHasChildInParent(false);
    }
  }, [setHasChildInParent]);

  const otherDeps = useRef({
    contextReadinessTimeoutInMilliseconds,
    setContextInSnowplowProvider,
    pause,
    resume,
  });

  const [isTimedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!hasChild) {
      setTimedOut(false);
    }
  }, [hasChild]);

  useEffect(() => {
    if (!isTimedOut) {
      const tid = setTimeout(
        () => setTimedOut(true),
        otherDeps.current.contextReadinessTimeoutInMilliseconds,
      );
      return () => {
        clearTimeout(tid);
      };
    }
  }, [isTimedOut]);

  if (isFunction(isContextReady)) {
    isContextReady = isContextReady(context);
  }

  const isReady = isTimedOut || isContextReady;
  useEffect(() => {
    if (!hasChild) {
      if (isReady) {
        otherDeps.current.setContextInSnowplowProvider(context);
        otherDeps.current.resume();
      } else {
        otherDeps.current.pause();
      }
    }
  }, [context, hasChild, isReady]);

  return (
    <HasChildSetterContext.Provider value={setHasChild}>
      <SnowplowPageContext.Provider value={value.current} children={children} />
    </HasChildSetterContext.Provider>
  );
};

export const useSnowplowPage = () => {
  const value = useContext(SnowplowPageContext);
  if (!value) {
    throw new Error(
      'useSnowplowPage() must be called within a <SnowplowPage />',
    );
  }
  return value;
};

export default SnowplowPage;
